import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  WizardFlow,
  WizardFlowType,
  WizardState,
  WizardSuccessProps,
} from '@/features/wizard';
import {
  feedbackWizardFlow,
  WizardStep,
  Employee,
  FeedbackFormData,
  FeedbackStatus,
  WizardAnswers,
  feedbackStepComponentMap,
  GoalImpact,
  Interaction,
  FeedbackInteraction,
} from '@momentum/types';
import {
  useEmployees,
  useCreateFeedback,
  useMyGoals,
  useUpdateFeedback,
} from '@/shared/hooks';
import {
  createFetchData,
  createPropsResolver,
  convertDomainStepToReactStep,
} from '@/routes/_dashboard/interactions/-components/WizardComponentUtils';
import { InteractionReviewStep } from './InteractionReviewStep';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { useValues } from '@/shared/hooks/useValues';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

interface FeedbackWizardProps {
  // Optional pre-selected employee
  initialEmployeeId?: string;
  existingInteractionId?: string;
  // Optional custom completion handler (if not provided, will submit to API and navigate)
  onComplete?: (interaction?: Interaction) => void;
  // Optional initial state
  initialState?: Partial<WizardState>;
  // Wizard UI configuration
  showProgress?: boolean;
  showNavigation?: boolean;
  autoStart?: boolean;
  // Loading/error message translations (can be overridden)
  loadingMessage?: string;
  errorMessage?: string;
  firstStepId?: string;
  flowId?: string;
}

export const FeedbackWizard = ({
  initialEmployeeId,
  existingInteractionId,
  onComplete,
  initialState: externalInitialState,
  showProgress = true,
  showNavigation = true,
  autoStart = true,
  loadingMessage,
  errorMessage,
  firstStepId,
  flowId = feedbackWizardFlow.id,
}: FeedbackWizardProps) => {
  const { t } = useTranslation('wizard');
  const { t: tCommon } = useTranslation('common');
  const employee = useEmployeeContext();
  const { data: employees, isLoading, error } = useEmployees({});
  const navigate = useNavigate();

  const {
    data: goals,
    isLoading: isLoadingGoals,
    error: errorLoadingGoals,
  } = useMyGoals(employee._id);
  const {
    data: companyValues,
    isLoading: isLoadingCompanyValues,
    error: errorLoadingCompanyValues,
  } = useValues(employee.companyId);
  const createFeedbackMutation = useCreateFeedback();
  const updateFeedbackMutation = useUpdateFeedback();

  // Convert the domain wizard flow to React wizard flow
  const wizardFlow: WizardFlowType<FeedbackFormData> = useMemo(() => {
    // Create fetch data with real employees
    const fetchData = createFetchData(
      employees || [],
      goals || [],
      companyValues || []
    );
    const resolver = createPropsResolver(fetchData);

    const createInteraction = async (
      props: WizardSuccessProps<FeedbackFormData>,
      isDraft: boolean
    ) => {
      // Prepare the data for the API call
      const employeeSelection = props.answers['employee-selection'] as Employee;
      const selectedEmployee = employees?.find(
        (p: Employee) => p._id === employeeSelection?._id
      );

      const goalSelection = props.answers['goal-selection'];
      const goalImpactRange = props.answers['goal-impact-range'];
      const companyValues = props.answers['company-values'];
      const additionalNegativeEffects = props.answers['additional-effects'];

      const feedbackData = {
        companyId: employee.companyId,
        creatorId: employee._id,
        receiverId: selectedEmployee?._id || '',
        goal:
          goalSelection && goalSelection !== 'no-effect'
            ? {
                id: goalSelection,
                impact: goalImpactRange
                  ? goalImpactRange === 3
                    ? GoalImpact.HIGH
                    : goalImpactRange === 2
                    ? GoalImpact.MEDIUM
                    : GoalImpact.LOW
                  : undefined,
              }
            : null,
        companyValues:
          companyValues?.map((value: string) => ({
            value,
            // need to remove this field from the DB
            impact: 1,
          })) || [],
        furtherImpact:
          additionalNegativeEffects?.map((effect: string) => ({
            id: effect,
            affected: ['recipient'], // Default to recipient, could be made more dynamic
          })) || [],
        isDraft,
        status: isDraft ? FeedbackStatus.DRAFT : FeedbackStatus.SENT,
        formData: props.answers, // Pass the complete form data
      };

      try {
        let feedback: FeedbackInteraction | undefined;
        if (existingInteractionId) {
          feedback = await updateFeedbackMutation.mutateAsync({
            id: existingInteractionId,
            data: feedbackData,
          });
        } else {
          feedback = await createFeedbackMutation.mutateAsync(feedbackData);
        }
        toast.success(t('feedbackQuestions.feedbackSubmittedSuccessfully'));
        if (onComplete) {
          onComplete(feedback);
        } else {
          sessionStorage.removeItem(flowId);
          navigate({ to: '/' });
        }
      } catch (error) {
        toast.error(t('feedbackQuestions.feedbackSubmittedFailed'), {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    };

    return {
      id: flowId,
      title: t('feedbackQuestions.title'),
      description: t('feedbackQuestions.description'),
      firstStepId: feedbackWizardFlow.firstStepId,
      steps: feedbackWizardFlow.steps.map((step) =>
        convertDomainStepToReactStep(
          step as WizardStep<FeedbackFormData>,
          resolver,
          t,
          employee
        )
      ),
      successComponent: (props) => (
        <InteractionReviewStep
          {...props}
          answers={props.answers as WizardAnswers<FeedbackFormData>}
          employees={employees || []}
          interactionType="feedback"
          companyValues={companyValues || []}
          onSendInteraction={async () => {
            console.log('Sending feedback with answers:', props.answers);
            await createInteraction(props, false);
          }}
          onSaveDraft={async () => {
            console.log('Sending DRAFT feedback with answers:', props.answers);
            await createInteraction(props, true);
          }}
          isSubmitting={createFeedbackMutation.isPending}
        />
      ),
      onComplete: (answers) => {
        // Just log completion, actual submission happens in the review component
        console.log(
          'Feedback Questions Wizard completed with answers:',
          answers
        );
      },
    };
  }, [t, employees, createFeedbackMutation, goals, companyValues]);

  // Prepare initial state if employeeId is provided
  const initialState = useMemo<Partial<WizardState>>(() => {
    if (externalInitialState) {
      return externalInitialState;
    }

    if (initialEmployeeId && employees && employees.length > 0) {
      const selectedEmployee = employees.find(
        (emp: Employee) => emp._id === initialEmployeeId
      );
      if (selectedEmployee) {
        return {
          answers: {
            'employee-selection': selectedEmployee,
          },
        };
      }
    }
    return {};
  }, [initialEmployeeId, employees, externalInitialState]);

  // Handle loading and error states
  // Wait for employees to load if employeeId is provided
  if (
    isLoading ||
    isLoadingGoals ||
    isLoadingCompanyValues ||
    (initialEmployeeId && !employees)
  ) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">
            {loadingMessage ?? tCommon('loadingData')}
          </div>
        </div>
      </div>
    );
  }

  if (error || errorLoadingGoals || errorLoadingCompanyValues) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            {errorMessage ?? tCommon('errorLoadingData')}{' '}
            {error?.message ||
              errorLoadingGoals?.message ||
              errorLoadingCompanyValues?.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <WizardFlow
      flow={wizardFlow}
      showNavigation={showNavigation}
      showProgress={showProgress}
      autoStart={autoStart}
      initialState={initialState}
      firstStepId={firstStepId}
      reason={{
        title: t('stepTitles.reason-details'),
        steps: Object.keys(feedbackStepComponentMap).filter(
          (key) =>
            ![
              'employee-selection',
              'date-selection',
              'reason-selection',
              'goal-selection',
              'goal-impact-range',
              'company-values',
              'additional-effects',
              'negative-effects-details',
              'organizational-impact-range',
            ].includes(key)
        ) as (keyof FeedbackFormData)[],
        afterStep: 'reason-selection',
      }}
    />
  );
};
